/**
 * Orbit Phase 6a / 6a.1 — pure helpers (popover copy, ETA, result lines, toast dedupe).
 */

import { useEffect, useState } from "react";
import { styleDisplayLabel } from "@/lib/styleDisplayLabel";

export type OrbitJobStatus =
  | "queued"
  | "processing"
  | "waiting"
  | "done"
  | "error"
  | string;

export type OrbitListJob = {
  id: string;
  style_id: string | null;
  parent_job_id: string | null;
  status: OrbitJobStatus;
  stage: string | null;
  /** Current Seedance attempt (1 | 2); null when unknown. */
  attempt?: number | null;
  /** ISO of current attempt submit; ETA prefers this over created_at. */
  attempt_started_at?: string | null;
  result: unknown;
  error: string | null;
  viewed: boolean;
  /** HD manifest available (Phase 5 list item). */
  hd_ready?: boolean;
  /** Fixture / test orbit (payload.orbit_fixture). */
  is_fixture?: boolean;
  created_at: string;
  updated_at: string;
};

export type OrbitJobResultShape = {
  flagged?: boolean;
  backtrack_kept?: boolean;
  top_turned?: boolean;
  missing?: string[];
  cut_off?: string[];
  identity_checked?: number;
  identity_failed?: string[];
};

export function isOrbitRunningStatus(status: string | null | undefined): boolean {
  return status === "queued" || status === "processing" || status === "waiting";
}

export function orbitStageLine(stage: string | null | undefined): string {
  switch (stage) {
    case "submit":
      return "Preparing…";
    case "await_video":
      return "Filming the orbit…";
    case "process":
    case "process_hd":
      return "Building the frames…";
    case "finalize":
      return "Saving…";
    default:
      return "Preparing…";
  }
}

/** Estimate 6 min from attempt_started_at (fallback created_at); never below "almost done". */
export function orbitTimeLeftLabel(
  startedAt: string | null | undefined,
  nowMs: number = Date.now(),
  estimateMs = 6 * 60 * 1000,
): string {
  if (!startedAt) return "about 6 min left";
  const start = Date.parse(startedAt);
  if (!Number.isFinite(start)) return "about 6 min left";
  const remaining = estimateMs - (nowMs - start);
  if (remaining <= 30_000) return "almost done";
  const mins = Math.max(1, Math.ceil(remaining / 60_000));
  return `about ${mins} min left`;
}

/**
 * Shared running-line formatter (popover, caption, tooltip).
 * Attempt ≥ 2 → "Second attempt · <time left>"; else just the time-left label.
 * Clock base: attempt_started_at, fallback created_at.
 */
export function orbitAttemptTimeLeftLine(
  job: Pick<OrbitListJob, "attempt" | "attempt_started_at" | "created_at"> | null | undefined,
  nowMs: number = Date.now(),
): string {
  const started =
    (typeof job?.attempt_started_at === "string" && job.attempt_started_at) ||
    job?.created_at ||
    null;
  const left = orbitTimeLeftLabel(started, nowMs);
  const attempt = typeof job?.attempt === "number" ? job.attempt : 1;
  if (attempt >= 2) return `Second attempt · ${left}`;
  return left;
}

/**
 * Single 15s clock for Orbit ETA (popover + showcase caption).
 * Owned by StageAngles; pass the returned nowMs into both surfaces.
 */
export function useOrbitNow(
  enabled: boolean,
  intervalMs = 15_000,
): number {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    setNowMs(Date.now());
    const t = window.setInterval(() => setNowMs(Date.now()), intervalMs);
    return () => window.clearInterval(t);
  }, [enabled, intervalMs]);
  return nowMs;
}

const VIEW_LABEL: Record<string, string> = {
  lateral: "Outside",
  outside: "Outside",
  medial: "Inside",
  inside: "Inside",
  top: "Top",
  bottom: "Outsole",
  outsole: "Outsole",
  "3q-front": "Front Quarter",
  front_quarter: "Front Quarter",
  front: "Front",
  back: "Back",
};

export function formatOrbitViewList(views: string[] | undefined | null): string {
  if (!views || views.length === 0) return "";
  return views.map((v) => VIEW_LABEL[v] ?? v).join(", ");
}

export function orbitResultLines(result: unknown): {
  clean: boolean;
  flagged: boolean;
  primary: string;
  missingLine: string | null;
  cutOffLine: string | null;
  pathCheckLine: string | null;
  topTurnedLine: string | null;
  identityLine: string | null;
  identityAmber: boolean;
} {
  const r = (result ?? null) as OrbitJobResultShape | null;
  const missing = Array.isArray(r?.missing) ? r!.missing! : [];
  const cutOff = Array.isArray(r?.cut_off) ? r!.cut_off! : [];
  const pathCheckLine = r?.backtrack_kept
    ? "Path check: order not confirmed"
    : null;
  const topTurnedLine = r?.top_turned === true ? "Top view turned" : null;
  const identityFailed = Array.isArray(r?.identity_failed)
    ? r!.identity_failed!
    : [];
  const identityChecked =
    typeof r?.identity_checked === "number" ? r.identity_checked : null;

  let identityLine: string | null = null;
  let identityAmber = false;
  if (identityFailed.length > 0) {
    identityLine = `Design difference: ${formatOrbitViewList(identityFailed)}`;
    identityAmber = true;
  } else if (identityChecked != null && identityChecked > 0) {
    identityLine = `Identity checked · ${identityChecked} of ${identityChecked} views`;
    identityAmber = false;
  }

  // Timeline soft-flags only (ignore result.flagged — also set by identity_failed).
  const timelineFlagged =
    missing.length > 0 || cutOff.length > 0 || pathCheckLine != null;
  const softAmber = identityAmber || topTurnedLine != null;
  const flagged =
    timelineFlagged || identityFailed.length > 0 || topTurnedLine != null;

  const missLabel = formatOrbitViewList(missing);
  const cutLabel = formatOrbitViewList(cutOff);

  if (!timelineFlagged) {
    return {
      clean: !softAmber,
      flagged: softAmber,
      primary: "Checked: all views present",
      missingLine: null,
      cutOffLine: null,
      pathCheckLine: null,
      topTurnedLine,
      identityLine,
      identityAmber,
    };
  }

  return {
    clean: false,
    flagged: true,
    primary: missLabel
      ? `Not confirmed: ${missLabel}`
      : pathCheckLine
        ? "Checked: all views present"
        : "Not confirmed",
    missingLine: missLabel ? `Not confirmed: ${missLabel}` : null,
    cutOffLine: cutLabel ? `Cut off: ${cutLabel}` : null,
    pathCheckLine,
    topTurnedLine,
    identityLine,
    identityAmber,
  };
}

export function parseOrbitFixtureFromSearch(
  search: string,
): { scenario: "good" | "bad" | "error"; delay_s: number } | null {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const raw = params.get("orbitFixture");
  if (raw !== "good" && raw !== "bad" && raw !== "error") return null;
  return { scenario: raw, delay_s: 60 };
}

/** Job-derived popover/frame kind — anglesStale never overrides status (6a.1 F-4). */
export type OrbitPopoverKind = "running" | "done" | "error" | "none";

export function resolveOrbitPopoverKind(input: {
  /** Ignored — kept so call sites can keep passing it. */
  anglesStale?: boolean;
  job: OrbitListJob | null | undefined;
}): OrbitPopoverKind {
  const status = input.job?.status;
  if (isOrbitRunningStatus(status)) return "running";
  if (status === "done") return "done";
  if (status === "error") return "error";
  return "none";
}

export type ShowcaseFrameMode = "none" | "running" | "done" | "error";

export function showcaseFrameMode(input: {
  anglesStale?: boolean;
  job: OrbitListJob | null | undefined;
}): ShowcaseFrameMode {
  return resolveOrbitPopoverKind({ job: input.job });
}

export const ORBIT_STALE_ACTION_MSG =
  "Multi-View is from an earlier master — regenerate Multi-View first.";

export const ORBIT_DESIGN_CHANGED_MSG =
  "Your design changed — this Orbit will show the previous version.";

/** Running orbit built from a different Multi-View than the current one. */
export function orbitShowsPreviousVersion(input: {
  anglesStale: boolean;
  job: OrbitListJob | null | undefined;
  anglesJobId: string | null | undefined;
}): boolean {
  if (!isOrbitRunningStatus(input.job?.status)) return false;
  if (input.anglesStale) return true;
  const parent = input.job?.parent_job_id;
  const current = input.anglesJobId;
  return !!parent && !!current && parent !== current;
}

export type ShowcaseCaptionModel = {
  base: string;
  amberSuffix: string | null;
};

/** Under-frame caption for Showcase (6b.3 S-3). */
export function showcaseCaptionModel(input: {
  job: OrbitListJob | null | undefined;
  anglesJobId: string | null | undefined;
  nowMs: number;
}): ShowcaseCaptionModel {
  const mode = showcaseFrameMode({ job: input.job });
  if (mode === "none") {
    return { base: "No Orbit yet — open the Orbit chip", amberSuffix: null };
  }
  if (mode === "running") {
    return {
      base: `Orbit in progress · ${orbitAttemptTimeLeftLine(input.job, input.nowMs)}`,
      amberSuffix: null,
    };
  }
  if (mode === "error") {
    return {
      base: "Orbit couldn't be completed — open the Orbit chip",
      amberSuffix: null,
    };
  }
  let base = "Orbit ready · click the image to open";
  const result = orbitResultLines(input.job?.result);
  if (result.flagged) {
    // P-2: short flagged caption only — detail lines stay in popover / Orbit window.
    base = "Orbit ready · flagged · click the image to open";
  }
  const earlier =
    !!input.job?.parent_job_id &&
    !!input.anglesJobId &&
    input.job.parent_job_id !== input.anglesJobId;
  return {
    base,
    amberSuffix: earlier ? " · From an earlier Multi-View" : null,
  };
}

export function styleOrbitLabel(
  hubStyles: Array<{
    styles: Array<{
      id: string;
      name: string | null;
      style_number: number | null;
    }>;
  }>,
  styleId: string | null | undefined,
): string | null {
  if (!styleId) return null;
  for (const col of hubStyles) {
    for (const s of col.styles) {
      if (s.id !== styleId) continue;
      // P-3: Hub rule — name wins; never "<number> <name>".
      return styleDisplayLabel({
        name: s.name,
        style_number: s.style_number,
      });
    }
  }
  return null;
}

/** Deep-link path for a style on Showcase. */
export function orbitShowcaseDeepLink(styleId: string): string {
  return `/footwear-studio?styleId=${encodeURIComponent(styleId)}&stage=showcase`;
}

export const ORBIT_OPEN_EVENT = "orbit:open";

export type OrbitOpenDetail = {
  jobId: string;
  styleId: string | null;
  handled: boolean;
};

export function dispatchOrbitOpen(detail: {
  jobId: string;
  styleId: string | null;
}): OrbitOpenDetail {
  const payload: OrbitOpenDetail = {
    jobId: detail.jobId,
    styleId: detail.styleId,
    handled: false,
  };
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(ORBIT_OPEN_EVENT, { detail: payload }),
    );
  }
  return payload;
}

/** Route an Orbit "Open" action after the CustomEvent is dispatched. */
export function decideOrbitOpenAction(args: {
  eventStyleId: string | null;
  activeStyleId: string | null;
  onStudioPage: boolean;
}): "open-showcase" | "new-tab" | "navigate" {
  if (!args.onStudioPage) return "navigate";
  if (
    args.eventStyleId &&
    args.activeStyleId &&
    args.eventStyleId === args.activeStyleId
  ) {
    return "open-showcase";
  }
  return "new-tab";
}

/* ── Toast dedupe across tabs (6a.1 F-6) ─────────────────────────── */

export const ORBIT_TOASTED_KEY_PREFIX = "orbit-toasted:";
export const ORBIT_TOASTED_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type OrbitToastStorage = {
  length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

function defaultOrbitToastStorage(): OrbitToastStorage | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

/** Drop toasted keys older than 7 days. Safe no-op if storage throws. */
export function pruneOrbitToastedKeys(
  nowMs: number = Date.now(),
  storage: OrbitToastStorage | null = defaultOrbitToastStorage(),
): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith(ORBIT_TOASTED_KEY_PREFIX)) keys.push(k);
    }
    for (const k of keys) {
      const raw = storage.getItem(k);
      const ts = raw != null ? Number(raw) : NaN;
      if (!Number.isFinite(ts) || nowMs - ts > ORBIT_TOASTED_TTL_MS) {
        storage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
}

/**
 * Check-and-set localStorage so only one tab toasts a given job.
 * Returns true → show the toast. Storage unavailable / throws → true (show).
 */
export function claimOrbitToastSlot(
  jobId: string,
  nowMs: number = Date.now(),
  storage: OrbitToastStorage | null = defaultOrbitToastStorage(),
): boolean {
  if (!storage) return true;
  try {
    pruneOrbitToastedKeys(nowMs, storage);
    const key = `${ORBIT_TOASTED_KEY_PREFIX}${jobId}`;
    if (storage.getItem(key) != null) return false;
    storage.setItem(key, String(nowMs));
    return true;
  } catch {
    return true;
  }
}

/* ── Phase 6b viewer helpers ─────────────────────────────────────── */

export const ORBIT_MARKER_DEFS = [
  { key: "sole", label: "Sole" },
  { key: "outside", label: "Outside" },
  { key: "front_quarter", label: "Front quarter" },
  { key: "inside", label: "Inside" },
  { key: "top", label: "Top" },
] as const;

export type OrbitMarkerKey = (typeof ORBIT_MARKER_DEFS)[number]["key"];

export type OrbitMarkers = Partial<Record<OrbitMarkerKey, number | null>>;

export function clampOrbitFrameIndex(index: number, frameCount: number): number {
  if (frameCount <= 0) return 0;
  return Math.max(0, Math.min(frameCount - 1, index));
}

/**
 * P-4 missing-outside fallback (shared by open + "Back to outside view"):
 * outside marker → midpoint(sole, front_quarter) → manifest open_index → 0.
 */
export function resolveOutsideOrOpenFallback(
  markers: OrbitMarkers | null | undefined,
  openIndex: number | null | undefined,
  frameCount: number,
): number {
  if (frameCount <= 0) return 0;
  const outside = markers?.outside;
  if (typeof outside === "number" && Number.isFinite(outside)) {
    return clampOrbitFrameIndex(Math.round(outside), frameCount);
  }
  const sole = markers?.sole;
  const fq = markers?.front_quarter;
  if (
    typeof sole === "number" &&
    Number.isFinite(sole) &&
    typeof fq === "number" &&
    Number.isFinite(fq)
  ) {
    return clampOrbitFrameIndex(Math.round((sole + fq) / 2), frameCount);
  }
  if (typeof openIndex === "number" && Number.isFinite(openIndex)) {
    return clampOrbitFrameIndex(Math.round(openIndex), frameCount);
  }
  return 0;
}

/** Open index — same missing-outside rule as Back to outside. */
export function resolveOrbitOpenIndex(
  markers: OrbitMarkers | null | undefined,
  openIndex: number | null | undefined,
  frameCount: number,
): number {
  return resolveOutsideOrOpenFallback(markers, openIndex, frameCount);
}

/** Resolve the Outside marker index for "Back to outside view". */
export function resolveOutsideFrameIndex(
  markers: OrbitMarkers | null | undefined,
  openIndex: number | null | undefined,
  frameCount: number,
): number {
  return resolveOutsideOrOpenFallback(markers, openIndex, frameCount);
}

/** Max concurrent frame decodes (Orbit 6b.1 F-4). */
export const ORBIT_DECODE_POOL_SIZE = 6;

/**
 * Within a stride set, pick indices that maximize coverage of the range
 * (greedy farthest-from-already-chosen). `seed` is already loaded (open_index).
 */
export function coverageVisitOrder(
  indices: readonly number[],
  seed: number,
): number[] {
  const remaining = new Set(indices);
  remaining.delete(seed);
  if (remaining.size === 0) return [];
  const chosen: number[] = [seed];
  const out: number[] = [];
  while (remaining.size > 0) {
    let best: number | null = null;
    let bestScore = -1;
    for (const i of remaining) {
      let minDist = Infinity;
      for (const c of chosen) {
        const d = Math.abs(i - c);
        if (d < minDist) minDist = d;
      }
      if (minDist > bestScore) {
        bestScore = minDist;
        best = i;
      }
    }
    remaining.delete(best!);
    chosen.push(best!);
    out.push(best!);
  }
  return out;
}

function strideIndexSet(frameCount: number, stride: number): number[] {
  const s = new Set<number>();
  for (let i = 0; i < frameCount; i += stride) s.add(i);
  if (frameCount > 0) s.add(frameCount - 1);
  return [...s].sort((a, b) => a - b);
}

/**
 * Coarse-to-fine preload order (F-4):
 * open_index → every-8th set → every-4th → every-2nd → rest.
 * Within each stride phase, visit in coverage order so far-end frames
 * land early enough for Slow-4G scrubbing (set is still 0,8,16,…,last).
 */
export function progressivePreloadOrder(
  openIndex: number,
  frameCount: number,
): number[] {
  if (frameCount <= 0) return [];
  const start = clampOrbitFrameIndex(openIndex, frameCount);
  const out: number[] = [];
  const seen = new Set<number>();
  const push = (i: number) => {
    if (i < 0 || i >= frameCount || seen.has(i)) return;
    seen.add(i);
    out.push(i);
  };
  push(start);
  for (const stride of [8, 4, 2, 1]) {
    const set = strideIndexSet(frameCount, stride);
    for (const i of coverageVisitOrder(set, start)) push(i);
  }
  return out;
}

/** Indices that make up the coarse (every-8th + open + last) set. */
export function coarsePreloadIndexSet(
  openIndex: number,
  frameCount: number,
): Set<number> {
  const set = new Set<number>();
  if (frameCount <= 0) return set;
  const start = clampOrbitFrameIndex(openIndex, frameCount);
  set.add(start);
  for (const i of strideIndexSet(frameCount, 8)) set.add(i);
  return set;
}

/**
 * Run async work over `items` with at most `concurrency` in flight.
 * Stops scheduling when `shouldContinue` returns false (cancel).
 */
export async function runBoundedPool<T>(
  items: readonly T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<void>,
  shouldContinue: () => boolean = () => true,
): Promise<void> {
  if (items.length === 0 || concurrency <= 0) return;
  let next = 0;
  const n = Math.min(concurrency, items.length);
  const runners = Array.from({ length: n }, async () => {
    while (shouldContinue()) {
      const i = next++;
      if (i >= items.length) return;
      await worker(items[i]!, i);
    }
  });
  await Promise.all(runners);
}

export function nearestLoadedFrameIndex(
  target: number,
  loaded: ReadonlyArray<boolean>,
): number {
  const n = loaded.length;
  if (n === 0) return -1;
  const t = clampOrbitFrameIndex(target, n);
  if (loaded[t]) return t;
  for (let d = 1; d < n; d++) {
    if (t - d >= 0 && loaded[t - d]) return t - d;
    if (t + d < n && loaded[t + d]) return t + d;
  }
  return -1;
}

/**
 * Drag mapping (6b.2 B-4): full path = 1.3 × frame width covers all frames.
 * Positive dx (drag right) advances the index (towards Top).
 * Result is rounded to the nearest frame (no glide).
 */
export const ORBIT_DRAG_SWEEP_FACTOR = 1.3;

export function frameIndexFromDrag(args: {
  startIndex: number;
  dxPx: number;
  frameWidthPx: number;
  frameCount: number;
}): number {
  const { startIndex, dxPx, frameWidthPx, frameCount } = args;
  if (frameCount <= 1 || frameWidthPx <= 0) {
    return clampOrbitFrameIndex(startIndex, frameCount);
  }
  const fullPath = ORBIT_DRAG_SWEEP_FACTOR * frameWidthPx;
  const delta = (dxPx / fullPath) * (frameCount - 1);
  return clampOrbitFrameIndex(Math.round(startIndex + delta), frameCount);
}

/** Continuous (unrounded) index delta for proofs / scrubbing previews. */
export function frameDeltaFromDragPx(args: {
  dxPx: number;
  frameWidthPx: number;
  frameCount: number;
}): number {
  const { dxPx, frameWidthPx, frameCount } = args;
  if (frameCount <= 1 || frameWidthPx <= 0) return 0;
  return (dxPx / (ORBIT_DRAG_SWEEP_FACTOR * frameWidthPx)) * (frameCount - 1);
}

export function frameIndexFromBarClick(args: {
  clientX: number;
  barLeft: number;
  barWidth: number;
  frameCount: number;
}): number {
  const { clientX, barLeft, barWidth, frameCount } = args;
  if (frameCount <= 1 || barWidth <= 0) return 0;
  const t = (clientX - barLeft) / barWidth;
  return clampOrbitFrameIndex(Math.round(t * (frameCount - 1)), frameCount);
}

/** Caption: nearest marker within ±3 frames, else "A → B". */
export function orbitViewCaption(
  index: number,
  markers: OrbitMarkers | null | undefined,
): string {
  const present = ORBIT_MARKER_DEFS.map((d) => {
    const i = markers?.[d.key];
    return typeof i === "number" && Number.isFinite(i)
      ? { label: d.label, index: i }
      : null;
  }).filter(Boolean) as Array<{ label: string; index: number }>;

  if (present.length === 0) return `Frame ${index + 1}`;

  let best = present[0]!;
  let bestDist = Math.abs(best.index - index);
  for (const m of present) {
    const d = Math.abs(m.index - index);
    if (d < bestDist) {
      best = m;
      bestDist = d;
    }
  }
  if (bestDist <= 3) return best.label;

  const sorted = [...present].sort((a, b) => a.index - b.index);
  const prev = [...sorted].reverse().find((m) => m.index <= index);
  const next = sorted.find((m) => m.index > index);
  if (prev && next) return `${prev.label} → ${next.label}`;
  if (prev) return prev.label;
  if (next) return next.label;
  return best.label;
}

export type OrbitMarkerLayout = {
  key: string;
  label: string;
  index: number;
  pct: number;
  /** Dot centre X in px within the bar. */
  xPx: number;
  /** Label centre X after clamping inside the bar. */
  labelXPx: number;
  /** Estimated label width in px. */
  labelW: number;
  /** 1 = below the line, 2 = second row with stem. */
  row: 1 | 2;
};

const LABEL_CHAR_PX = 6.2;
const LABEL_PAD_PX = 8;
const LABEL_GAP_PX = 6;

function estimateLabelWidth(label: string): number {
  return Math.ceil(label.length * LABEL_CHAR_PX + LABEL_PAD_PX);
}

/**
 * B-3: present markers only. Dots on the track; labels below (row 1).
 * If a row-1 label would overlap the previous row-1 label (gap < 6 px),
 * it moves to row 2. Labels clamped inside the bar width.
 */
export function layoutOrbitMarkers(
  markers: OrbitMarkers | null | undefined,
  frameCount: number,
  barWidthPx = 640,
): OrbitMarkerLayout[] {
  if (frameCount <= 0 || barWidthPx <= 0) return [];
  const present: OrbitMarkerLayout[] = [];
  for (const d of ORBIT_MARKER_DEFS) {
    const i = markers?.[d.key];
    if (typeof i !== "number" || !Number.isFinite(i)) continue;
    const index = clampOrbitFrameIndex(i, frameCount);
    const pct = frameCount <= 1 ? 0 : (index / (frameCount - 1)) * 100;
    const xPx = frameCount <= 1 ? 0 : (index / (frameCount - 1)) * barWidthPx;
    const labelW = estimateLabelWidth(d.label);
    const half = labelW / 2;
    const labelXPx = Math.min(
      Math.max(xPx, half),
      Math.max(half, barWidthPx - half),
    );
    present.push({
      key: d.key,
      label: d.label,
      index,
      pct,
      xPx,
      labelXPx,
      labelW,
      row: 1,
    });
  }
  present.sort((a, b) => a.xPx - b.xPx);

  const row1Right: number[] = [];
  for (const cur of present) {
    const left = cur.labelXPx - cur.labelW / 2;
    const right = cur.labelXPx + cur.labelW / 2;
    let collide = false;
    for (const r of row1Right) {
      if (left < r + LABEL_GAP_PX) {
        collide = true;
        break;
      }
    }
    if (collide) {
      cur.row = 2;
    } else {
      cur.row = 1;
      row1Right.push(right);
    }
  }
  return present;
}

/** B-5: ease-out cubic; duration min(900, 120 + 8 × frames travelled). */
export function easeOutCubic(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return 1 - (1 - x) ** 3;
}

export function orbitSeekDurationMs(framesTravelled: number): number {
  return Math.min(900, 120 + 8 * Math.abs(framesTravelled));
}

/**
 * object-contain rect: whole image visible, letterboxed, never cropped.
 */
export function containedImageRect(args: {
  frameW: number;
  frameH: number;
  imageW: number;
  imageH: number;
}): { x: number; y: number; w: number; h: number; scale: number } {
  const { frameW, frameH, imageW, imageH } = args;
  if (frameW <= 0 || frameH <= 0 || imageW <= 0 || imageH <= 0) {
    return { x: 0, y: 0, w: 0, h: 0, scale: 0 };
  }
  const scale = Math.min(frameW / imageW, frameH / imageH);
  const w = imageW * scale;
  const h = imageH * scale;
  return {
    x: (frameW - w) / 2,
    y: (frameH - h) / 2,
    w,
    h,
    scale,
  };
}

export function nextOrbitPlayStep(args: {
  index: number;
  dir: 1 | -1;
  frameCount: number;
  loop: boolean;
}): { index: number; dir: 1 | -1; stop: boolean } {
  const { index, dir, frameCount, loop } = args;
  if (frameCount <= 1) return { index: 0, dir, stop: !loop };
  const next = index + dir;
  if (next >= 0 && next < frameCount) {
    return { index: next, dir, stop: false };
  }
  if (!loop) {
    return {
      index: clampOrbitFrameIndex(index, frameCount),
      dir,
      stop: true,
    };
  }
  const newDir = (dir === 1 ? -1 : 1) as 1 | -1;
  return {
    index: clampOrbitFrameIndex(index + newDir, frameCount),
    dir: newDir,
    stop: false,
  };
}

/** P-5 — window play speed. 0.5×=12fps, 1×=24fps, 2×=24fps × 2 frames/tick. */
export type OrbitPlaySpeed = 0.5 | 1 | 2;

export function orbitPlaySpeedConfig(speed: OrbitPlaySpeed): {
  fps: number;
  step: number;
} {
  if (speed === 0.5) return { fps: 12, step: 1 };
  if (speed === 2) return { fps: 24, step: 2 };
  return { fps: 24, step: 1 };
}

export function orbitPlayAdvance(args: {
  index: number;
  dir: 1 | -1;
  frameCount: number;
  loop: boolean;
  step: number;
}): { index: number; dir: 1 | -1; stop: boolean } {
  let { index, dir } = args;
  let stop = false;
  const n = Math.max(1, Math.floor(args.step) || 1);
  for (let i = 0; i < n; i++) {
    const s = nextOrbitPlayStep({
      index,
      dir,
      frameCount: args.frameCount,
      loop: args.loop,
    });
    index = s.index;
    dir = s.dir;
    stop = s.stop;
    if (stop) break;
  }
  return { index, dir, stop };
}

/** P-1 — zero-height rect at the stage-row bottom, left = Showcase button. */
export const ORBIT_POPOVER_SIDE_OFFSET_PX = 8;
export const ORBIT_POPOVER_COLLISION_PADDING_PX = 16;

function makeAnchorRect(
  x: number,
  y: number,
  width: number,
  height: number,
): DOMRect {
  const right = x + width;
  const bottom = y + height;
  if (typeof DOMRect === "function") {
    return new DOMRect(x, y, width, height);
  }
  return {
    x,
    y,
    width,
    height,
    top: y,
    left: x,
    right,
    bottom,
    toJSON() {
      return { x, y, width, height, top: y, left: x, right, bottom };
    },
  } as DOMRect;
}

export function orbitPopoverAnchorRect(
  showcase: { left: number; width: number } | null | undefined,
  row: { bottom: number } | null | undefined,
): DOMRect {
  const left = showcase?.left ?? 0;
  const width = Math.max(0, showcase?.width ?? 0);
  const top = row?.bottom ?? 0;
  return makeAnchorRect(left, top, width, 0);
}

/** Chip hover/focus tooltip copy (V-7). */
export function orbitChipTooltipText(input: {
  kind: OrbitPopoverKind;
  job: OrbitListJob | null | undefined;
  nowMs: number;
}): string {
  switch (input.kind) {
    case "running":
      return `${orbitStageLine(input.job?.stage)} · ${orbitAttemptTimeLeftLine(input.job, input.nowMs)}`;
    case "done":
      return "Orbit ready — click to open";
    case "error":
      return "Orbit couldn't be completed — click for details";
    case "none":
    default:
      return "Orbit — turn the shoe and see every side, including sole and top.";
  }
}

export function orbitStyleNumberSlug(input: {
  styleNumber?: number | null;
  styleName?: string | null;
  styleId?: string | null;
}): string {
  if (input.styleNumber != null && Number.isFinite(input.styleNumber)) {
    return String(input.styleNumber);
  }
  const name = (input.styleName ?? "").trim();
  if (name) {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    if (slug) return slug;
  }
  if (input.styleId) {
    return input.styleId.replace(/-/g, "").slice(0, 8).toLowerCase();
  }
  return "style";
}

export function orbitVideoDownloadName(styleSlug: string): string {
  // SPEC R-7: style-<n>-orbit.mp4 (<n> from styleDisplayLabel's number / slug).
  const n = styleSlug.replace(/^style-/i, "");
  return `style-${n}-orbit.mp4`;
}

export function orbitFramesZipName(styleSlug: string): string {
  const n = styleSlug.replace(/^style-/i, "");
  return `style-${n}-orbit-frames.zip`;
}

export function orbitFrameJpgBasename(
  styleSlug: string,
  viewOrFrame: string,
): string {
  const n = styleSlug.replace(/^style-/i, "");
  const view = viewOrFrame
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "frame";
  return `style-${n}-orbit-${view}`;
}

export function orbitFrameSequenceBasenames(frameCount: number): string[] {
  return Array.from({ length: frameCount }, (_, i) =>
    String(i + 1).padStart(4, "0"),
  );
}

export function orbitResultChipText(result: unknown): {
  text: string;
  amber: boolean;
} {
  const lines = orbitResultLines(result);
  const onlyIdentity =
    lines.identityLine &&
    !lines.missingLine &&
    !lines.cutOffLine &&
    !lines.pathCheckLine &&
    !lines.topTurnedLine;
  if (onlyIdentity) {
    return { text: lines.identityLine!, amber: lines.identityAmber };
  }
  const onlyTopTurned =
    lines.topTurnedLine &&
    !lines.missingLine &&
    !lines.cutOffLine &&
    !lines.pathCheckLine &&
    !lines.identityLine;
  if (onlyTopTurned) {
    return { text: lines.topTurnedLine!, amber: true };
  }
  if (lines.clean) return { text: "Checked: all views", amber: false };
  const parts = [
    lines.missingLine ??
      (lines.pathCheckLine || lines.cutOffLine ? lines.primary : null),
    lines.cutOffLine,
    lines.pathCheckLine,
    lines.topTurnedLine,
    lines.identityLine,
  ].filter(Boolean);
  if (parts.length === 0 && lines.topTurnedLine) {
    return { text: lines.topTurnedLine, amber: true };
  }
  return {
    text: (parts.length ? parts : [lines.primary]).join(" · "),
    amber: true,
  };
}

export function orbitQualityChipLabel(input: {
  quality: "720" | "hd" | null;
  hdReady: boolean;
}): string {
  if (input.quality === "hd") return "HD";
  if (input.quality === "720" && !input.hdReady) return "720p · HD on the way";
  if (input.quality === "720") return "720p";
  return "…";
}

/** Refetch signed URLs after 50 min (they expire at 60). */
export const ORBIT_FRAMES_REFETCH_MS = 50 * 60 * 1000;
